'use client'

import React from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'

type BaseDialogOptions = {
  title?: React.ReactNode
  description?: React.ReactNode
  confirmLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
}

type ConfirmOptions = BaseDialogOptions & {
  confirmLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
}

type AlertOptions = BaseDialogOptions & {
  confirmLabel?: React.ReactNode
}

type PromptOptions = BaseDialogOptions & {
  defaultValue?: string
  placeholder?: string
  confirmLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
}

type DialogRequest =
  | {
      type: 'alert'
      options: AlertOptions
      resolve: () => void
    }
  | {
      type: 'confirm'
      options: ConfirmOptions
      resolve: (confirmed: boolean) => void
    }
  | {
      type: 'prompt'
      options: PromptOptions
      resolve: (value: string | null) => void
    }

type DialogContextValue = {
  alert: (options: AlertOptions) => Promise<void>
  confirm: (options: ConfirmOptions) => Promise<boolean>
  prompt: (options: PromptOptions) => Promise<string | null>
}

const DialogContext = React.createContext<DialogContextValue | null>(null)

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = React.useState<DialogRequest[]>([])
  const currentRequest = queue[0] ?? null
  const [promptValue, setPromptValue] = React.useState('')

  React.useEffect(() => {
    if (currentRequest?.type === 'prompt') {
      setPromptValue(currentRequest.options.defaultValue ?? '')
    }
  }, [currentRequest])

  const dequeue = React.useCallback(() => {
    setQueue(prev => prev.slice(1))
  }, [])

  const alert = React.useCallback((options: AlertOptions) => {
    return new Promise<void>(resolve => {
      setQueue(prev => [
        ...prev,
        { type: 'alert', options, resolve: () => resolve() },
      ])
    })
  }, [])

  const confirm = React.useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      setQueue(prev => [
        ...prev,
        { type: 'confirm', options, resolve: confirmed => resolve(confirmed) },
      ])
    })
  }, [])

  const prompt = React.useCallback((options: PromptOptions) => {
    return new Promise<string | null>(resolve => {
      setQueue(prev => [
        ...prev,
        { type: 'prompt', options, resolve: value => resolve(value) },
      ])
    })
  }, [])

  const handleClose = React.useCallback(() => {
    if (!currentRequest) {
      return
    }

    if (currentRequest.type === 'alert') {
      currentRequest.resolve()
    }

    if (currentRequest.type === 'confirm') {
      currentRequest.resolve(false)
    }

    if (currentRequest.type === 'prompt') {
      currentRequest.resolve(null)
    }

    dequeue()
  }, [currentRequest, dequeue])

  const handleConfirm = React.useCallback(() => {
    if (!currentRequest) {
      return
    }

    if (currentRequest.type === 'alert') {
      currentRequest.resolve()
    }

    if (currentRequest.type === 'confirm') {
      currentRequest.resolve(true)
    }

    if (currentRequest.type === 'prompt') {
      currentRequest.resolve(promptValue)
    }

    dequeue()
  }, [currentRequest, dequeue, promptValue])

  const value = React.useMemo(
    () => ({
      alert,
      confirm,
      prompt,
    }),
    [alert, confirm, prompt]
  )

  const isOpen = Boolean(currentRequest)
  const { title, description, confirmLabel, cancelLabel } =
    currentRequest?.options ?? {}

  return (
    <DialogContext.Provider value={value}>
      {children}

      <AlertDialog
        open={isOpen}
        onOpenChange={open => {
          if (!open && currentRequest) {
            handleClose()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            {title ? <AlertDialogTitle>{title}</AlertDialogTitle> : null}
            {description ? (
              <AlertDialogDescription className="whitespace-pre-line">
                {description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          {currentRequest?.type === 'prompt' ? (
            <Input
              autoFocus
              value={promptValue}
              onChange={event => setPromptValue(event.target.value)}
              placeholder={currentRequest.options.placeholder}
            />
          ) : null}
          <AlertDialogFooter>
            {currentRequest?.type !== 'alert' ? (
              <AlertDialogCancel
                onClick={event => {
                  event.preventDefault()
                  handleClose()
                }}
              >
                {cancelLabel ?? 'Cancel'}
              </AlertDialogCancel>
            ) : null}
            <AlertDialogAction
              onClick={event => {
                event.preventDefault()
                handleConfirm()
              }}
            >
              {confirmLabel ??
                (currentRequest?.type === 'alert'
                  ? 'OK'
                  : currentRequest?.type === 'prompt'
                    ? 'Submit'
                    : 'Confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider>
  )
}

export const useDialog = () => {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }

  return context
}
